import React, { useContext, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";

import { ActionButton, Intro, Modal, Text } from "components/commons";
import { generateReportAdmin, getCashIns, batchManualCashIn } from "apis";
import { ErrorCode, Path, Role, UserAccess } from "enums";
import useDataTable from "hooks/useDataTable";
import locale from "localization";

import { cashInColumns } from "./cashin-columns";
import CashInFilter from "./cashin-filter";
import { cashInFilterState } from "./cashin-filter.state";
import { apiMapper, mapDataToList, mapFilterToRequest } from "./cashin.mapper";
import CashInList from "./cashin.list";
import useExport from "hooks/useExport";
import { SuccessModal, ConfirmModal } from "components/modals";
import { UserContext } from "contexts";
import { useModal, useApi } from "hooks";
import CashInImportModal from "./cashin-import";
import { formatPesoWithDecimalRange, handleRequest } from "utils";
import VoidCashInModal from "./void-cashin-modal";
import { updateCashInRemarks, voidCashIn } from "../../../apis/cash-in.api";

const CashInModule = () => {
  const { sessionId, userAccess, user } = useContext(UserContext);

  const getUserAccess = userAccess.find((item) => {
    return item.key === UserAccess.PLBCashIn;
  });
  const cashinImportModal = useModal();
  const cashinImportModalResult = useModal();
  const voidTransactionModal = useModal();
  const confirmModal = useModal();

  const [remarks, setRemarks] = useState("");
  const [value, setValue] = useState("");

  const { request: voidRequest, loading: voidingRequest } = useApi({
    api: voidCashIn,
    pageError: false,
    modalError: true,
    handleOwnError: true,
  });

  const updateRemarksApi = useApi({
    api: updateCashInRemarks,
    modalError: true,
    pageError: false,
  });

  const onVoidTransactionCb = useCallback(
    async (payload = {}) => {
      try {
        const cashIn = payload.cashInItem || payload.redemptionItem || voidTransactionModal.cashins;
        const submittedRemarks = payload.value;

        if (!cashIn) {
          return;
        }

        await voidRequest({
          cashinId: cashIn.cashinId || cashIn.cashInId,
          remarks: submittedRemarks,
        });
        voidTransactionModal.close();
        table.fetch({});

        confirmModal.show({
          title: locale.casInSuccessfullyVoided,
          content: (
            <locale.Populate
              text={locale.transactionHasBeenVoidedCashIn}
              items={[<b>{formatPesoWithDecimalRange(cashIn?.amount || 0, 2)}</b>]}
            />
          ),
          loading: voidingRequest,
          cashInItem: cashIn,
        });
        setRemarks("");
      } catch (err) {
        const errorCode =
          err?.data?.errorCode || err?.data?.code || err?.data?.messageCode || err?.errorCode;

        if (
          errorCode === ErrorCode.TxnVoidingWindowExpired ||
          err?.data?.message?.includes?.(ErrorCode.TxnVoidingWindowExpired)
        ) {
          voidTransactionModal.close();
          confirmModal.show({
            title: locale.unableToVoidTransaction,
            content: locale.voidingWindowExpiredMessage,
            primary: {
              text: locale.gotIt,
              onClick: () => {
                confirmModal.close();
              },
            },
          });
          return;
        }

        return err.showError?.();
      }
    },
    //eslint-disable-next-line
    [voidRequest, voidTransactionModal, voidingRequest, confirmModal]
  );
  const isFrontline = getUserAccess?.role === Role.Frontline;

  const { filter, search, table } = useDataTable({
    api: { api: getCashIns, mapper: apiMapper },
    filter: { initialState: cashInFilterState(), mapper: mapFilterToRequest },
    table: { key: "cashins", mapper: (redemptions) =>
        mapDataToList(
          redemptions,
          setRemarks,
          voidTransactionModal,
          onVoidTransactionCb,
          updateRemarksApi,
          setValue,
          getUserAccess,
          user
        ), columns: isFrontline ? cashInColumns.filter(col => col.key !== "options") : cashInColumns },
  });

  const { request: batchManualCashInRequest, loading: batchManualCashInLoading } = useApi({
    api: batchManualCashIn,
    handleOwnError: true,
  });

  const exportReport = useExport({
    api: generateReportAdmin,
    reportType: "cashin",
    hasModal: true,
    mappedFilterState: {
      ...apiMapper(filter?.mappedFilterState),
      platformType: "plb",
      searchKey: search?.searchKey,
      connectionId: sessionId,
    },
  });

  const history = useHistory();

  const openImportCb = useCallback(() => {
    cashinImportModal.show();
  }, [cashinImportModal]);

  const submitCashInManual = (data, importModal) => {
    handleRequest(
      async () => {
        await batchManualCashInRequest({
          data: [...data],
        });

        cashinImportModalResult.show({
          title: locale.manualCashInImportSuccess,
          children: (
            <div>
              <Text normal align="center" style={{ marginBottom: "20px", marginTop: "20px" }}>
                {locale.detailsHaveBeenSuccessfullyUpdated}
              </Text>
              <ActionButton
                center
                items={[
                  {
                    text: locale.gotIt,
                    primary: true,
                    onClick: () => {
                      cashinImportModalResult.close();
                    },
                  },
                ]}
              />
            </div>
          ),
        });

        table.refetch();
        importModal.close();
      },
      {
        C1020: (e) => {
          importModal.close();
          cashinImportModalResult.show({
            title: locale.manualCashInImportFailed,
            children: (
              <div>
                <Text
                  normal
                  align="center"
                  style={{ marginBottom: "20px" }}
                >{`${locale.unableToImportCheckTheFollowingEntries}: `}</Text>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <Text label style={{ marginRight: "40px" }}>{`${locale.invalidEntries}:`}</Text>
                  <Text normal strong>{`${
                    e?.invalidRowList.length
                  } (rows no. ${e?.invalidRowList.join()})`}</Text>
                </div>
                <ActionButton
                  center
                  items={[
                    {
                      text: locale.retry,
                      primary: true,
                      onClick: () => {
                        cashinImportModalResult.close();
                      },
                    },
                  ]}
                />
              </div>
            ),
          });
        },
      }
    );
  };
  return (
    <>
      <SuccessModal {...exportReport?.successModalComponent} />
      <CashInImportModal
        {...cashinImportModal}
        submitCashInManual={submitCashInManual}
        batchManualCashInLoading={batchManualCashInLoading}
      />
      <Modal {...cashinImportModalResult} />
      <ConfirmModal {...confirmModal} />
      <VoidCashInModal
        {...voidTransactionModal}
        remarks={remarks}
        onChangeRemarks={(v) => {
          if (v.length <= 255) {
            setRemarks(v);
          }
        }}
        loading={voidingRequest || updateRemarksApi.loading}
        onSubmit={voidTransactionModal.submit}
        value={value}
        setValue={setValue}
      />
      <div>
        <Intro
          actionText={
            getUserAccess?.fullAccess || getUserAccess?.modifyAccess
              ? locale.createManualCashIn
              : undefined
          }
          actionOnClick={
            getUserAccess?.fullAccess || getUserAccess?.modifyAccess
              ? () => history.push(Path.ManualCashIn)
              : undefined
          }
          title={locale.plbCashIn}
          subtitle={locale.viewTrackCashActivities}
        />
      </div>
      <CashInFilter
        {...filter}
        {...search}
        {...exportReport}
        openImport={openImportCb}
        getUserAccess={getUserAccess}
      />
      <CashInList table={table} />
    </>
  );
};

export default CashInModule;
